import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";

import {
  checkResetTokenService,
  forgotPasswordService,
  getMeContextService,
  loginService,
  registerService,
  resetPasswordService,
} from "@/services/auth.service";
import { IAPIResponse } from "@/services/api.types";
import {
  AuthResponse,
  ForgotPasswordFormData,
  ForgotPasswordResponse,
  LoginFormData,
  MeContextResponse,
  RegisterFormData,
  ResetPasswordFormData,
} from "@/types/authentication.types";

export const authQueryKeys = {
  all: () => ["auth"] as const,
  me: () => [...authQueryKeys.all(), "me"] as const,
  meContext: () => [...authQueryKeys.all(), "me-context"] as const,
  resetToken: (token: string) => [...authQueryKeys.all(), "reset-token", token] as const,
};

export const usePostLogin = () => {
  return useMutation<IAPIResponse<AuthResponse>, Error, LoginFormData>({
    mutationFn: loginService,
    retry: false,
  });
};

export const usePostRegister = () => {
  return useMutation<IAPIResponse<AuthResponse>, Error, RegisterFormData>({
    mutationFn: registerService,
    retry: false,
  });
};

export const usePostForgotPassword = () => {
  return useMutation<IAPIResponse<ForgotPasswordResponse>, Error, ForgotPasswordFormData>({
    mutationFn: forgotPasswordService,
    retry: false,
    onError: err => toast.error(err.message),
  });
};

export const usePostResetPassword = () => {
  return useMutation<IAPIResponse<AuthResponse>, Error, ResetPasswordFormData>({
    mutationFn: resetPasswordService,
    retry: false,
    onError: err => toast.error(err.message),
  });
};

export const useGetMeContext = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<MeContextResponse, AxiosError>({
    queryKey: authQueryKeys.meContext(),
    queryFn: getMeContextService,
    enabled,
  });
};

export const useCheckResetToken = (token: string) => {
  return useQuery({
    queryKey: authQueryKeys.resetToken(token),
    queryFn: () => checkResetTokenService(token),
    enabled: !!token,
    retry: false,
  });
};
